import { formatCurrency } from './utils';

/**
 * Shared snippet variable substitution, used by every composer that inserts
 * snippets. Substitution happens at INSERT time (not send time) so the user
 * always sees and can edit the final text before sending.
 *
 * Lead variables fill from the customer's most recent open CRM lead. When the
 * customer has no open lead they are replaced with an empty string and
 * reported back so the composer can warn the user — never send a raw
 * "{{leadStage}}" token to a customer.
 */

export interface SnippetCustomerData {
  first_name?: string | null;
  last_name?: string | null;
  phone?: string | null;
  email?: string | null;
}

export interface SnippetLeadData {
  title?: string | null;
  value?: number | null;
  stage_name?: string | null;
  pipeline_name?: string | null;
  source?: string | null;
}

export const SNIPPET_CUSTOMER_VARIABLES = [
  { label: 'First Name', value: '{{firstName}}' },
  { label: 'Last Name', value: '{{lastName}}' },
  { label: 'Full Name', value: '{{fullName}}' },
  { label: 'Phone', value: '{{phone}}' },
  { label: 'Email', value: '{{email}}' },
];

export const SNIPPET_LEAD_VARIABLES = [
  { label: 'Lead Title', value: '{{leadTitle}}' },
  { label: 'Lead Value', value: '{{leadValue}}' },
  { label: 'Lead Stage', value: '{{leadStage}}' },
  { label: 'Lead Pipeline', value: '{{leadPipeline}}' },
  { label: 'Lead Source', value: '{{leadSource}}' },
];

const LEAD_TOKEN_KEYS = ['leadTitle', 'leadValue', 'leadStage', 'leadPipeline', 'leadSource'] as const;

/** True when the snippet text contains any lead variable token. */
export function contentHasLeadVariables(content: string): boolean {
  return LEAD_TOKEN_KEYS.some((key) => content.includes(`{{${key}}}`));
}

export interface SnippetSubstitutionResult {
  text: string;
  /** Lead tokens that were present but could not be filled (no open lead). */
  missingLeadVariables: string[];
}

export function replaceSnippetVariables(
  content: string,
  context: { customer?: SnippetCustomerData | null; lead?: SnippetLeadData | null }
): SnippetSubstitutionResult {
  let result = content;
  const { customer, lead } = context;

  if (customer) {
    result = result.replace(/\{\{firstName\}\}/g, customer.first_name || '');
    result = result.replace(/\{\{lastName\}\}/g, customer.last_name || '');
    result = result.replace(
      /\{\{fullName\}\}/g,
      `${customer.first_name || ''} ${customer.last_name || ''}`.trim()
    );
    result = result.replace(/\{\{phone\}\}/g, customer.phone || '');
    result = result.replace(/\{\{email\}\}/g, customer.email || '');
  }

  const missingLeadVariables: string[] = [];
  const leadValues: Record<(typeof LEAD_TOKEN_KEYS)[number], string> = {
    leadTitle: lead?.title || '',
    leadValue: lead && lead.value != null ? formatCurrency(Number(lead.value)) : '',
    leadStage: lead?.stage_name || '',
    leadPipeline: lead?.pipeline_name || '',
    leadSource: lead?.source || '',
  };

  for (const key of LEAD_TOKEN_KEYS) {
    const token = `{{${key}}}`;
    if (!result.includes(token)) continue;
    if (!lead) missingLeadVariables.push(token);
    // Always strip lead tokens — a blank beats sending "{{leadStage}}" to a customer.
    result = result.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), lead ? leadValues[key] : '');
  }

  return { text: result, missingLeadVariables };
}
